"use client"

import React, { useState, useEffect } from 'react';
import { Input } from "@/components/ui/custom/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/custom/ui/select";
import { Checkbox } from "@/components/ui/custom/ui/checkbox";
import { Label } from "@/components/ui/custom/ui/label";
import { Search, ChevronDown, ChevronUp } from "lucide-react";

const Filters = ({ filters, setFilters, products }) => {
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState(['ALL']);
  const [rarities, setRarities] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(false); // Mobile filters open by default

  // Define rarity order
  const rarityOrder = ['standard', 'rare', 'epic', 'legendary', 'mythical'];

  useEffect(() => {
    const uniqueCategories = [...new Set(products.map(product => product.category))];
    setCategories(uniqueCategories);

    // Get unique currencies from default_currency
    const uniqueCurrencies = ['ALL', ...new Set(
      products.map(product => product.default_currency?.symbol).filter(Boolean)
    )];
    setCurrencies(uniqueCurrencies);

    // Get unique rarities from products and sort them according to rarityOrder
    const uniqueRarities = [...new Set(
      products.map(product => product.rarity).filter(Boolean)
    )];
    
    // Sort rarities based on the predefined order
    const sortedRarities = uniqueRarities.sort((a, b) => {
      const indexA = rarityOrder.indexOf(a.toLowerCase());
      const indexB = rarityOrder.indexOf(b.toLowerCase());
      return indexA - indexB;
    });

    setRarities(sortedRarities);
  }, [products]);

  const handleChange = (type, value) => {
    setFilters(prev => ({
      ...prev,
      [type]: value
    }));
  };

  return (
    <div className="bg-metaxseed-black border border-metaxseed-blue rounded-lg p-6 w-full md:w-1/4 lg:w-1/5">
      {/* Mobile Collapsible Header */}
      <div className="md:hidden mb-4">
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="flex items-center justify-between w-full text-white font-bold text-lg hover:text-metaxseed-blue transition-colors"
        >
          <span>Filters</span>
          {isCollapsed ? (
            <ChevronDown className="w-5 h-5" />
          ) : (
            <ChevronUp className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Desktop Header */}
      <h2 className="hidden md:block text-white font-bold text-lg mb-6">Filters</h2>

      {/* Mobile Filters */}
      <div className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
        isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[800px] opacity-100'
      }`}>
        <div className="space-y-4">
          {/* Search - Always visible on mobile */}
          <div className="space-y-2">
            <Label className="text-metaxseed-text-gray text-sm">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-metaxseed-text-gray w-4 h-4" />
              <Input
                placeholder="Search..."
                onChange={(e) => handleChange('name', e.target.value)}
                className="pl-10 bg-metaxseed-dark-gray border-metaxseed-blue focus:border-metaxseed-blue text-white placeholder:text-metaxseed-text-gray"
              />
            </div>
          </div>

          {/* Currency - Always visible on mobile */}
          <div className="space-y-2">
            <Label className="text-metaxseed-text-gray text-sm">Currency</Label>
            <Select value={filters.currency} onValueChange={(value) => handleChange('currency', value)}>
              <SelectTrigger className="bg-metaxseed-dark-gray border-metaxseed-blue focus:border-metaxseed-blue text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-metaxseed-dark-gray border-metaxseed-blue">
                {currencies.map(currency => (
                  <SelectItem key={currency} value={currency} className="text-white hover:bg-metaxseed-content-gray">
                    {currency}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sort By - Always visible on mobile */}
          <div className="space-y-2">
            <Label className="text-metaxseed-text-gray text-sm">Sort by</Label>
            <Select value={filters.sort} onValueChange={(value) => handleChange('sort', value)}>
              <SelectTrigger className="bg-metaxseed-dark-gray border-metaxseed-blue focus:border-metaxseed-blue text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-metaxseed-dark-gray border-metaxseed-blue">
                {['New Arrivals', 'Price: Low to High', 'Price: High to Low'].map(option => (
                  <SelectItem key={option} value={option} className="text-white hover:bg-metaxseed-content-gray">
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Categories */}
          <div className="space-y-3">
            <Label className="text-metaxseed-text-gray text-sm">Categories</Label>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((category, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Checkbox
                    id={`mobile-${category}`}
                    checked={filters.category.includes(category)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        if (!filters.category.includes(category)) {
                          setFilters(prev => ({
                            ...prev,
                            category: [...prev.category, category]
                          }));
                        }
                      } else {
                        setFilters(prev => ({
                          ...prev,
                          category: prev.category.filter(c => c !== category)
                        }));
                      }
                    }}
                    className="border-metaxseed-content-gray data-[state=checked]:bg-metaxseed-blue data-[state=checked]:border-metaxseed-blue"
                  />
                  <Label
                    htmlFor={`mobile-${category}`}
                    className="text-metaxseed-text-gray text-xs cursor-pointer hover:text-white transition-colors"
                  >
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Rarity */}
          <div className="space-y-3">
            <Label className="text-metaxseed-text-gray text-sm">Rarity</Label>
            <div className="grid grid-cols-2 gap-2">
              {rarities.map(rarity => (
                <div key={rarity} className="flex items-center space-x-2">
                  <Checkbox
                    id={`mobile-${rarity}`}
                    checked={filters.rarity.includes(rarity)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        if (!filters.rarity.includes(rarity)) {
                          setFilters(prev => ({
                            ...prev,
                            rarity: [...prev.rarity, rarity]
                          }));
                        }
                      } else {
                        setFilters(prev => ({
                          ...prev,
                          rarity: prev.rarity.filter(r => r !== rarity)
                        }));
                      }
                    }}
                    className="border-metaxseed-content-gray data-[state=checked]:bg-metaxseed-blue data-[state=checked]:border-metaxseed-blue"
                  />
                  <Label
                    htmlFor={`mobile-${rarity}`}
                    className="text-metaxseed-text-gray text-xs cursor-pointer hover:text-white transition-colors"
                  >
                    {rarity.charAt(0).toUpperCase() + rarity.slice(1)}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Filters */}
      <div className="hidden md:block">
        <div className="space-y-6">
        {/* Search */}
        <div className="space-y-2">
          <Label className="text-metaxseed-text-gray text-sm">Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-metaxseed-text-gray w-4 h-4" />
            <Input
              placeholder="Search..."
              onChange={(e) => handleChange('name', e.target.value)}
              className="pl-10 bg-metaxseed-dark-gray border-metaxseed-blue focus:border-metaxseed-blue text-white placeholder:text-metaxseed-text-gray"
            />
          </div>
        </div>

        {/* Currency */}
        <div className="space-y-2">
          <Label className="text-metaxseed-text-gray text-sm">Currency</Label>
          <Select value={filters.currency} onValueChange={(value) => handleChange('currency', value)}>
            <SelectTrigger className="bg-metaxseed-dark-gray border-metaxseed-blue focus:border-metaxseed-blue text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-metaxseed-dark-gray border-metaxseed-blue">
              {currencies.map(currency => (
                <SelectItem key={currency} value={currency} className="text-white hover:bg-metaxseed-gray">
                  {currency}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Sort By */}
        <div className="space-y-2">
          <Label className="text-metaxseed-text-gray text-sm">Sort by</Label>
          <Select value={filters.sort} onValueChange={(value) => handleChange('sort', value)}>
            <SelectTrigger className="bg-metaxseed-dark-gray border-metaxseed-blue focus:border-metaxseed-blue text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-metaxseed-dark-gray border-metaxseed-blue">
              {['New Arrivals', 'Price: Low to High', 'Price: High to Low'].map(option => (
                <SelectItem key={option} value={option} className="text-white hover:bg-metaxseed-gray">
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Categories */}
        <div className="space-y-3">
          <Label className="text-metaxseed-text-gray text-sm">Categories</Label>
          <div className="space-y-2">
            {categories.map((category, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Checkbox
                  id={category}
                  checked={filters.category.includes(category)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      if (!filters.category.includes(category)) {
                        setFilters(prev => ({
                          ...prev,
                          category: [...prev.category, category]
                        }));
                      }
                    } else {
                      setFilters(prev => ({
                        ...prev,
                        category: prev.category.filter(c => c !== category)
                      }));
                    }
                  }}
                  className="border-metaxseed-content-gray data-[state=checked]:bg-metaxseed-blue data-[state=checked]:border-metaxseed-blue"
                />
                <Label
                  htmlFor={category}
                  className="text-metaxseed-text-gray text-sm cursor-pointer hover:text-white transition-colors"
                >
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Rarity */}
        <div className="space-y-3">
          <Label className="text-metaxseed-text-gray text-sm">Rarity</Label>
          <div className="space-y-2">
            {rarities.map(rarity => (
              <div key={rarity} className="flex items-center space-x-2">
                <Checkbox
                  id={rarity}
                  checked={filters.rarity.includes(rarity)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      if (!filters.rarity.includes(rarity)) {
                        setFilters(prev => ({
                          ...prev,
                          rarity: [...prev.rarity, rarity]
                        }));
                      }
                    } else {
                      setFilters(prev => ({
                        ...prev,
                        rarity: prev.rarity.filter(r => r !== rarity)
                      }));
                    }
                  }}
                  className="border-metaxseed-content-gray data-[state=checked]:bg-metaxseed-blue data-[state=checked]:border-metaxseed-blue"
                />
                <Label
                  htmlFor={rarity}
                  className="text-metaxseed-text-gray text-sm cursor-pointer hover:text-white transition-colors"
                >
                  {rarity.charAt(0).toUpperCase() + rarity.slice(1)}
                </Label>
              </div>
            ))}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default Filters;
